import { Eye, Target, Handshake, TrendingUp, Sparkles, Cog } from "lucide-react";

const MISSION_POINTS = [
  { icon: Target, text: "Manufacture products that exceed customer expectations." },
  { icon: Cog, text: "Continuously improve technology and manufacturing processes." },
  { icon: Handshake, text: "Build long-term partnerships through integrity and trust." },
  { icon: TrendingUp, text: "Expand our presence across domestic and international markets." },
];

export const VisionMission = () => {
  return (
    <section
      id="vision-mission"
      data-testid="vision-mission-section"
      className="relative bg-[#0B0E12] py-24 md:py-32 border-t border-[#F5F1EA]/8 overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-brand-red/[0.03] blur-[160px] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Vision */}
          <div
            data-testid="vision-card"
            className="relative bg-[#161A21] border border-[#F5F1EA]/10 p-10 md:p-14 hover:border-brand-red/40 transition-colors corner-accent"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-brand-red/10 border border-brand-red/30">
                <Eye size={22} className="text-brand-red" strokeWidth={1.5} />
              </div>
              <span className="text-[11px] uppercase tracking-[0.4em] text-brand-red font-medium font-sans">
                Our Vision
              </span>
            </div>

            <h3 className="font-display tracking-tight text-white text-2xl md:text-3xl lg:text-4xl leading-[1.15] font-medium">
              To become a <span className="italic text-brand-red font-normal">globally recognised</span> manufacturer of industrial cables and rubber hose solutions.
            </h3>

            <p className="mt-8 text-base text-white/60 leading-relaxed font-sans max-w-lg">
              Delivering products that set benchmarks in quality, safety, and innovation — carrying the promise of Indian manufacturing excellence to the world.
            </p>

            <div className="mt-10 flex items-center gap-3">
              <Sparkles size={16} className="text-brand-red" />
              <span className="text-xs uppercase tracking-[0.3em] text-white/50 font-sans">
                Setting Benchmarks
              </span>
            </div>
          </div>

          {/* Mission */}
          <div
            data-testid="mission-card"
            className="relative bg-[#161A21] border border-[#F5F1EA]/10 p-10 md:p-14 hover:border-brand-red/40 transition-colors"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-brand-red/10 border border-brand-red/30">
                <Target size={22} className="text-brand-red" strokeWidth={1.5} />
              </div>
              <span className="text-[11px] uppercase tracking-[0.4em] text-brand-red font-medium font-sans">
                Our Mission
              </span>
            </div>

            <h3 className="font-display tracking-tight text-white text-2xl md:text-3xl leading-[1.2] font-medium mb-8">
              Four principles that guide <span className="italic text-brand-red font-normal">everything we build</span>.
            </h3>

            <div className="space-y-5">
              {MISSION_POINTS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div
                    key={i}
                    data-testid={`mission-point-${i}`}
                    className="flex items-start gap-4 border-l-2 border-brand-red/40 pl-5 py-1"
                  >
                    <Icon size={18} className="text-brand-red mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-sm text-white/75 leading-relaxed font-sans">{p.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Commitment strip */}
        <div
          data-testid="commitment-strip"
          className="mt-16 border-t border-[#F5F1EA]/10 pt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.35em] text-brand-red font-medium font-sans mb-3">
              Our Commitment
            </div>
            <p className="text-base md:text-lg text-white/70 leading-relaxed font-sans">
              At Niryasa Industries, quality is not just a standard — it&apos;s our promise. Every product is engineered to deliver exceptional reliability, long service life, and consistent performance in demanding industrial environments.
            </p>
          </div>
          <div className="flex-shrink-0">
            <div className="font-display italic text-2xl md:text-3xl text-white font-medium leading-tight">
              Engineering Quality<br />
              <span className="text-brand-red">Delivering Trust.</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
